import {
  AnalyticsComponentProvider,
  AnalyticsScreenProvider,
  Card,
  Flex,
  KpiCard,
  PageGridCell,
  PageGridRow,
  StandardLayout,
  SwipeablePanel,
  useMediaQuery,
} from "@wayflyer/flyui";
import {
  ChartFilterSwitches,
  DatePickerHeader,
  TemporalResolutionPickers,
  TimeSeriesChart,
  type ChartFilterSwitchesProps,
  type DatePickerHeaderProps,
  type ReferencedMultiChartDataPoint,
  type TemporalResolutionPickersProps,
  type TimeSeriesChartLineColor,
  type ValueType,
} from "@wayflyer/flyui-dataviz";

import type { TikTokAccountData } from "./tiktok-spend";
import { MetricTypes } from "../../autogen/metrics";
import { getGroupNameWithId, getTikTokLinePoints, type TikTokSummaryItem } from "./tiktok-spend-datapoints";

export interface InternalTikTokSpendScreenProps {
  pageTitle: string;
  chartData: ReferencedMultiChartDataPoint[];
  datePickerHeaderProps: DatePickerHeaderProps;
  hint?: string;
  groups: string[];
  groupMapping: Record<string, number>;
  formatValue: (rawValue: ValueType, isRef?: boolean) => string;
  chartFilters: Omit<ChartFilterSwitchesProps, "switchOneName" | "switchTwoName"> &
    Partial<Pick<ChartFilterSwitchesProps, "switchOneName" | "switchTwoName">> &
    TemporalResolutionPickersProps;
  thisYearEnabled: boolean;
  lastYearEnabled: boolean;
  summaryData: TikTokSummaryItem[];
  accountData: TikTokAccountData;
  onSelectSummaryTab: (metricType: string, selected: boolean) => void;
}

const fullWidth = { xxs: { span: 6 }, sm: { span: 8 }, md: { span: 12 } };
const colors: TimeSeriesChartLineColor[] = [
  "var(--palette-dataviz-categorical-category1)",
  "var(--palette-dataviz-categorical-category2)",
  "var(--palette-dataviz-categorical-category3)",
  "var(--palette-dataviz-categorical-category4)",
  "var(--palette-dataviz-categorical-category5)",
  "var(--palette-dataviz-categorical-category6)",
  "var(--palette-dataviz-categorical-category7)",
];

export const InternalTikTokSpendScreen = ({
  pageTitle,
  chartData,
  datePickerHeaderProps,
  hint = "",
  groups,
  groupMapping,
  formatValue,
  chartFilters,
  thisYearEnabled,
  lastYearEnabled,
  summaryData,
  accountData,
  onSelectSummaryTab,
}: InternalTikTokSpendScreenProps) => {
  const query = useMediaQuery();
  const { onClickDateRes, hideMonthly, hideWeekly, selectedButtonIndex, ...switchProps } = chartFilters;
  const lines = groups.flatMap((group) => {
    const name = getGroupNameWithId(group, accountData);
    const color = colors[(groupMapping[group] ?? 0) % colors.length];
    return [
      ...(thisYearEnabled ? [{ period: "Current" as const, label: `${name} This Year` }] : []),
      ...(lastYearEnabled ? [{ period: "Reference" as const, label: `${name} Last Year` }] : []),
    ].map((line) => ({
      ...line,
      key: `${group}-${line.period}`,
      color,
      data: getTikTokLinePoints(chartData, group, line.period),
    }));
  });
  const snapPoints = chartData.map((row) => Date.parse(`${row.dayRaw}T00:00:00Z`));
  const labels = new Map(chartData.map((row) => [Date.parse(`${row.dayRaw}T00:00:00Z`), row.day]));
  const formatDate = (x: number) => labels.get(x) ?? "";

  return (
    <AnalyticsScreenProvider screen="internal-tiktok-spend">
      <StandardLayout title={pageTitle}>
        <PageGridRow>
          <PageGridCell layout={fullWidth}>
            <AnalyticsComponentProvider component="date-range">
              <DatePickerHeader
                {...datePickerHeaderProps}
                summary={query("sm").windowGt && hint ? hint : ""}
                useSimplePanel
                inlineLeft
              />
            </AnalyticsComponentProvider>
          </PageGridCell>
        </PageGridRow>
        <PageGridRow>
          <PageGridCell layout={fullWidth}>
            <AnalyticsComponentProvider component="account-selection">
              <SwipeablePanel
                scrollerAccessibilityLabel="TikTok spend by account"
                backwardScrollButtonAccessibilityLabel="Reveal previous accounts"
                forwardScrollButtonAccessibilityLabel="Reveal more accounts"
              >
                <Flex gap="4" wrap="nowrap">
                  {summaryData.map((item) => (
                    <Flex
                      key={item.metricType}
                      shrink={0}
                      width="min(calc(var(--sizes-spacing-36) * 2), calc(100vw - var(--sizes-spacing-12)))"
                    >
                      <KpiCard
                        title={
                          item.metricType === MetricTypes.TIKTOK_AD_SPEND
                            ? item.title
                            : getGroupNameWithId(item.metricType, accountData)
                        }
                        {...(item.rawMetricValue == null && item.trendValue == null
                          ? { amount: item.metricValue ?? "" }
                          : {
                              amount:
                                item.rawMetricValue == null
                                  ? { value: null }
                                  : {
                                      value: item.rawMetricValue,
                                      currencyCode: item.currencyCode ?? "USD",
                                      formatOptions: {
                                        minimumFractionDigits: item.fractionDigits ?? 0,
                                        maximumFractionDigits: item.fractionDigits ?? 0,
                                        currencyDisplay: "symbol",
                                      },
                                    },
                              trend:
                                item.trendValue == null
                                  ? undefined
                                  : {
                                      value: item.trendValue,
                                      iconVariant: "straight",
                                      status: item.metricReferenceSuccess ? "positive" : "critical",
                                      formatFn: () => item.metricReferenceValue ?? "",
                                    },
                            })}
                        subtext={
                          item.metricReferenceValue
                            ? `${item.trendValue == null ? `${item.metricReferenceValue} ` : ""}vs. last year`
                            : undefined
                        }
                        infoTooltip={item.tooltip}
                        isSelected={groups.includes(item.metricType)}
                        onAction={() => onSelectSummaryTab(item.metricType, !groups.includes(item.metricType))}
                      />
                    </Flex>
                  ))}
                </Flex>
              </SwipeablePanel>
            </AnalyticsComponentProvider>
          </PageGridCell>
        </PageGridRow>
        <PageGridRow>
          <PageGridCell layout={fullWidth}>
            <AnalyticsComponentProvider component="spend-chart">
              <Card
                slots={{
                  header: (
                    <Card.ChartHeader
                      title="Spend"
                      headingElement="h2"
                      slots={{
                        controls: (
                          <Flex gap="4" wrap="wrap" align="center">
                            <TemporalResolutionPickers
                              onClickDateRes={onClickDateRes}
                              hideWeekly={hideWeekly}
                              hideMonthly={hideMonthly}
                              selectedButtonIndex={selectedButtonIndex}
                              className="no-padding"
                            />
                            <ChartFilterSwitches
                              {...switchProps}
                              switchOneName={switchProps.switchOneName ?? "this-year"}
                              switchTwoName={switchProps.switchTwoName ?? "last-year"}
                              switchOneOn={thisYearEnabled}
                              switchTwoOn={lastYearEnabled}
                            />
                          </Flex>
                        ),
                      }}
                    />
                  ),
                }}
              >
                <TimeSeriesChart.Root
                  label="TikTok spend"
                  snapPoints={snapPoints}
                  xDomain={snapPoints.length ? [snapPoints[0], snapPoints[snapPoints.length - 1]] : undefined}
                  status={chartData.length ? "ready" : "empty"}
                >
                  <TimeSeriesChart.Plot formatX={formatDate} formatY={formatValue}>
                    {lines.map((line) => (
                      <TimeSeriesChart.Series
                        key={line.key}
                        seriesKey={line.key}
                        label={line.label}
                        color={line.color}
                        data={line.data}
                        curve="monotone"
                        before={line.period === "Reference" ? "dashed" : "solid"}
                      />
                    ))}
                    <TimeSeriesChart.Inspector>
                      {(point) => (
                        <TimeSeriesChart.Tooltip title={formatDate(point.x)}>
                          {point.entries.map((entry) => (
                            <TimeSeriesChart.TooltipRow
                              key={entry.seriesKey}
                              label={entry.label}
                              value={formatValue(entry.value, entry.seriesKey.endsWith("-Reference"))}
                            />
                          ))}
                        </TimeSeriesChart.Tooltip>
                      )}
                    </TimeSeriesChart.Inspector>
                  </TimeSeriesChart.Plot>
                  <TimeSeriesChart.Legend>
                    {lines.map((line) => (
                      <TimeSeriesChart.LegendItem
                        key={line.key}
                        color={line.color}
                        indicator={line.period === "Reference" ? "dashed" : "solid"}
                      >
                        {line.label}
                      </TimeSeriesChart.LegendItem>
                    ))}
                  </TimeSeriesChart.Legend>
                </TimeSeriesChart.Root>
              </Card>
            </AnalyticsComponentProvider>
          </PageGridCell>
        </PageGridRow>
      </StandardLayout>
    </AnalyticsScreenProvider>
  );
};
